// src/components/admin/layout/Header.js
import { useEffect, useMemo, useState } from "react";
import { Bell, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import toast from "react-hot-toast";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";

const API_BASE = (
  process.env.REACT_APP_API_URL || "http://localhost:5000"
).replace(/\/+$/, "");

export default function Header({ onMenuClick }) {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.isRead).length;
  }, [notifications]);

  const fetchNotifications = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/notifications`);

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(
          json?.message || "Impossible de charger les notifications",
        );
      }

      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error("Header notifications error:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();

    const userRaw = localStorage.getItem("user");
    let currentUser = null;

    try {
      currentUser = userRaw ? JSON.parse(userRaw) : null;
    } catch {
      currentUser = null;
    }

    const adminId = currentUser?.id ? String(currentUser.id) : "admin";
    const role = currentUser?.role || "admin";

    const socket = io(API_BASE, {
      auth: { userId: adminId, role },
      transports: ["websocket", "polling"],
    });

    socket.on("adminNotification", (notification) => {
      setNotifications((prev) => [notification, ...prev]);
      toast.success(notification?.title || "Nouvelle notification");
    });

    return () => {
      socket.off("adminNotification");
      socket.disconnect();
    };
  }, []);

  const handleNotificationsClick = () => {
    navigate("/admin/notifications");
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 h-16 z-30
      bg-white dark:bg-gray-800
      shadow-md border-b border-gray-200 dark:border-gray-700
      flex items-center justify-between
      px-4 md:px-6"
    >
      <div className="flex items-center space-x-3 min-w-0">
        <button
          type="button"
          onClick={onMenuClick}
          className="block md:hidden p-1.5 rounded-md text-blue-600 hover:bg-blue-100 dark:hover:bg-gray-700"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>

        <img src="/logo.png" alt="NGC Logo" className="h-8 w-auto" />
        <span className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white hidden sm:inline truncate">
          NGC Financial Services
        </span>
      </div>

      <div className="flex items-center space-x-4 sm:space-x-6">
        <button
          type="button"
          onClick={handleNotificationsClick}
          className="relative p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
          aria-label="Notifications"
          title="Notifications"
        >
          <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />

          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-semibold rounded-full px-1">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        <div className="flex items-center space-x-2">
          <img
            src="/admin-avatar.jpg"
            alt="Admin"
            className="w-8 h-8 rounded-full"
          />
          <div className="text-sm text-gray-800 dark:text-white hidden sm:block">
            <div>Admin Name</div>
            <div className="text-xs text-gray-500">Super Admin</div>
          </div>
        </div>
      </div>
    </header>
  );
}
